// The freeform corkboard (FreeformBoard on the Mac): cards where the writer puts them. Positions are stored on each
// node (`boardX`/`boardY`, per arrangement, synced); cards never placed sit in a grid below the placed ones. The first
// drop pins every card where it's shown, so the grid doesn't reflow. Moves show at once (`pending`) and are forgotten
// once the saved positions arrive. Arrange ▸ Order Contents by Position gives the folder the board's reading order;
// Tidy Up takes every card off the board. Option-arrow keys (Alt elsewhere) move the focused card a card's width or
// height, as the Mac's accessibility actions do. Read-only where the arrangement can't be restructured.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { OpenedProject, TreeNode } from '@writing-suite/core';
import { alternateSets } from '@writing-suite/core';
import {
  alternateSetForNode, boardColumns, boardPoints, CARD_HEIGHT, CARD_WIDTH, dropChanges, nudgeChanges, SPACING, type BoardPoint,
} from '@writing-suite/core/plan';
import { announce } from '../../shell/context.ts';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { navigate } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { planEdit } from '../../store/plan.ts';
import { storedPoints } from '../../store/planModel.ts';
import { isJournalPosts } from './posts.tsx';
import { boardFor, hasChildren } from '../helpers.ts';
import { MenuButton, MenuPopup } from '../Menu.tsx';
import type { PlanHeat } from './hooks.ts';
import { CardFace, castCardMenu, heatClass } from './IndexCard.tsx';
import { planItemMenu } from './menus.ts';
import { fadedCard } from '../Corkboard.tsx';

type Changes = Map<string, BoardPoint | null>;

export function FreeformBoard({ project, tree, node, arrangement, live, hrefFor, heat }: PageProps & { heat: PlanHeat }) {
  const ws = useWorkspace();
  const editable = !!ws?.canStructure && !!live;
  const board = boardFor(tree, node?.id ?? null, isJournalPosts(project, arrangement));
  const cards = board.cards;
  const heading = board.folder ? board.folder.title || 'Untitled' : arrangement.role === 'collection' ? arrangement.name : 'Top level';
  const fields = project.fields();
  const entities = project.entities();
  const sets = useMemo(() => ((project as OpenedProject).record ? alternateSets((project as OpenedProject).record) : []), [project]);

  const area = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth || 900);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stored = useMemo(() => storedPoints(project as OpenedProject, arrangement.id), [project, arrangement.id]);
  const [pending, setPending] = useState<Changes>(new Map());
  // The arrangement caught up: forget the moves it now shows.
  const storedKey = [...stored].map(([id, p]) => `${id}:${p.x},${p.y}`).join('|');
  useEffect(() => {
    setPending((old) => {
      if (!old.size) return old;
      const next = new Map([...old].filter(([id, p]) => {
        const s = stored.get(id);
        return p ? !s || s.x !== p.x || s.y !== p.y : !!s;
      }));
      return next.size === old.size ? old : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey]);

  const placed = new Map<string, BoardPoint>();
  for (const c of cards) {
    const p = pending.has(c.id) ? pending.get(c.id) : stored.get(c.id);
    if (p) placed.set(c.id, p);
  }
  const points = boardPoints(cards.map((c) => c.id), placed, boardColumns(width));
  const xs = [...points.values()].map((p) => p.x), ys = [...points.values()].map((p) => p.y);
  const boardWidth = Math.max(width, (xs.length ? Math.max(...xs) : 0) + CARD_WIDTH + SPACING * 4);
  const boardHeight = Math.max(400, (ys.length ? Math.max(...ys) : 0) + CARD_HEIGHT + SPACING * 4);

  const apply = (changes: Changes | null) => {
    if (!changes || !changes.size || !live) return;
    setPending((old) => new Map([...old, ...changes]));
    try {
      planEdit(live, (e) => e.setBoardPoints(arrangement.id, changes));
    } catch (err) {
      announce(err instanceof Error ? err.message : String(err));
    }
  };

  // Dragging a card: pointer events (a card is a link, so the browser's own drag is off).
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number } | null>(null);
  const start = useRef<{ id: string; x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const onPointerDown = (e: ReactPointerEvent, id: string) => {
    if (!editable || e.button !== 0 || ws?.renaming === id) return;
    start.current = { id, x: e.clientX, y: e.clientY, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const s = start.current;
    if (!s) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (!s.moved && Math.hypot(dx, dy) < 4) return;
    s.moved = true;
    setDrag({ id: s.id, dx, dy });
  };
  const onPointerUp = (e: ReactPointerEvent) => {
    const s = start.current;
    start.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (!s || !s.moved) {
      setDrag(null);
      return;
    }
    suppressClick.current = true;
    setTimeout(() => (suppressClick.current = false), 0);
    const from = points.get(s.id);
    setDrag(null);
    if (!from) return;
    const to = { x: Math.max(0, Math.round(from.x + e.clientX - s.x)), y: Math.max(0, Math.round(from.y + e.clientY - s.y)) };
    apply(dropChanges(s.id, to, points, placed));
  };

  const [menu, setMenu] = useState<{ x: number; y: number; id: string } | null>(null);
  const cardHref = (n: TreeNode) => (hasChildren(n) ? hrefFor('plan/corkboard', n.id) : hrefFor('write', n.id));

  return (
    <div className="corkboard-page" data-layout="freeform">
      <header className="page-head">
        <h1 tabIndex={-1}>{heading}</h1>
        <p className="page-sub">{cards.length === 1 ? '1 card' : `${cards.length} cards`}</p>
        <span className="page-actions">
          <MenuButton
            label="Arrange"
            className="quiet-button header-menu"
            disabled={!editable || cards.length === 0}
            title="Order the shelf by the board, or put the cards back in a grid"
            entries={[
              {
                label: 'Order Shelf by Position',
                onSelect: () => {
                  if (!live) return;
                  planEdit(live, (e) => e.orderByBoard(arrangement.id, points));
                  announce('The shelf follows the board');
                },
              },
              { label: 'Tidy Up', onSelect: () => apply(new Map(cards.map((c) => [c.id, null]))) },
            ]}
          >
            Arrange
          </MenuButton>
        </span>
      </header>
      {cards.length === 0 ? <p className="empty-note">Nothing in this folder.</p> : null}
      <div className="freeform-scroll" ref={area} role="region" aria-label={`Board of ${heading}`} tabIndex={-1}>
        <ul
          className="freeform-board"
          aria-label={`Cards in ${heading}`}
          ref={(el) => {
            el?.style.setProperty('width', `${boardWidth}px`);
            el?.style.setProperty('height', `${boardHeight}px`);
          }}
        >
          {cards.map((n) => {
            const p = points.get(n.id) ?? { x: 0, y: 0 };
            const moving = drag?.id === n.id;
            const x = p.x + (moving ? drag!.dx : 0), y = p.y + (moving ? drag!.dy : 0);
            const href = cardHref(n);
            return (
              <li
                key={n.id}
                className={`card free-card${n.id === board.selected ? ' selected' : ''}${moving ? ' moving' : ''}${heatClass(heat.heat[n.id])}${fadedCard(ws, n)}`}
                data-node-id={n.id}
                data-x={p.x}
                data-y={p.y}
                ref={(el) => {
                  el?.style.setProperty('left', `${x}px`);
                  el?.style.setProperty('top', `${y}px`);
                }}
                onPointerDown={(e) => onPointerDown(e, n.id)}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => {
                  start.current = null;
                  setDrag(null);
                }}
                onClickCapture={(e) => {
                  if (suppressClick.current) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onContextMenu={(e) => {
                  if (!ws) return;
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, id: n.id });
                }}
                onKeyDown={(e) => {
                  const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
                  if (step && e.altKey && editable) {
                    e.preventDefault();
                    apply(nudgeChanges(n.id, step[0], step[1], points, placed));
                  } else if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setMenu({ x: r.left + 24, y: r.top + 32, id: n.id });
                    e.preventDefault();
                  }
                }}
              >
                <a
                  href={href}
                  draggable={false}
                  aria-current={n.id === board.selected ? 'true' : undefined}
                  aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight Alt+ArrowUp Alt+ArrowDown"
                  onClick={(e) => {
                    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    // A tap selects a scene (the inspector follows it); a folder opens.
                    navigate(hasChildren(n) ? href : hrefFor('plan/corkboard', n.id));
                  }}
                  onDoubleClick={() => navigate(href)}
                >
                  <CardFace
                    project={project}
                    node={n}
                    fields={fields}
                    entities={entities}
                    look={{ heat: heat.heat[n.id], cold: heat.cold(n), coldDays: heat.coldDays, alternates: alternateSetForNode(sets, n.id)?.members.length }}
                  />
                </a>
              </li>
            );
          })}
        </ul>
      </div>
      {menu && ws ? (
        <MenuPopup
          label="Card"
          at={menu}
          entries={(() => {
            const cast = cards.find((c) => c.id === menu.id);
            return (cast && castCardMenu({ project, hrefFor }, cast)) ?? [
              { label: 'Open in Write', onSelect: () => navigate(hrefFor('write', menu.id)) },
              { kind: 'separator' },
              ...planItemMenu(ws, [menu.id], { onRename: (id) => ws.setRenaming(id), onCreated: (id) => ws.setRenaming(id) }),
            ];
          })()}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </div>
  );
}
